import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LaunchReadinessResult } from "@/features/tools/LaunchReadinessResult";
import { MonetisationModelResult } from "@/features/tools/MonetisationModelResult";
import { SalesHeadlineResult } from "@/features/tools/SalesHeadlineResult";
import { getProToolResult } from "@/features/tools/proToolApi";
import { getToolRoute } from "@/features/tools/toolMetadata";
import type {
  LaunchReadinessOutput,
  MonetisationModelOutput,
  SalesHeadlineOutput,
  ToolResultSummary,
} from "@/features/tools/proToolTypes";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatBand(value: string): string {
  return value.replace(/_/g, " ");
}

function formatStatusLabel(value: string): string {
  if (value === "complete") {
    return "Saved";
  }

  if (value === "failed") {
    return "Needs retry";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildSummaryText(result: ToolResultSummary): string {
  if (result.status === "failed") {
    return `${result.toolDisplayName}: this saved result did not complete.`;
  }

  if (result.tool_type === "launch_readiness_checker" && result.output_payload) {
    const output = result.output_payload as Partial<LaunchReadinessOutput>;

    return [
      `${result.toolDisplayName}`,
      typeof output.readinessScore === "number"
        ? `Score: ${output.readinessScore}/100`
        : null,
      typeof output.scoreBand === "string"
        ? `Band: ${formatBand(output.scoreBand)}`
        : null,
      output.summary ? `Summary: ${output.summary}` : null,
      output.recommendedNextStep
        ? `Next step: ${output.recommendedNextStep}`
        : null,
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (
    result.tool_type === "monetisation_model_suggester" &&
    result.output_payload
  ) {
    const output = result.output_payload as Partial<MonetisationModelOutput>;

    return [
      `${result.toolDisplayName}`,
      output.recommendedModel?.label
        ? `Recommended model: ${output.recommendedModel.label}`
        : null,
      output.positioningAngle ? `Positioning: ${output.positioningAngle}` : null,
      output.pricingGuidance?.suggestedRange
        ? `Pricing: ${output.pricingGuidance.suggestedRange}`
        : null,
      output.recommendedNextStep
        ? `Next step: ${output.recommendedNextStep}`
        : null,
      output.summary ? `Summary: ${output.summary}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (
    result.tool_type === "sales_page_headline_generator" &&
    result.output_payload
  ) {
    const output = result.output_payload as Partial<SalesHeadlineOutput>;

    return [
      `${result.toolDisplayName}`,
      output.recommendedTopPick?.headline
        ? `Top headline: ${output.recommendedTopPick.headline}`
        : null,
      output.recommendedTopPick?.reason
        ? `Reason: ${output.recommendedTopPick.reason}`
        : null,
      output.recommendedNextStep
        ? `Next step: ${output.recommendedNextStep}`
        : null,
      output.summary ? `Summary: ${output.summary}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  }

  return `${result.toolDisplayName}: ${result.status}`;
}

function isLaunchReadinessOutput(value: unknown): value is LaunchReadinessOutput {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const output = value as Partial<LaunchReadinessOutput>;

  return (
    typeof output.readinessScore === "number" &&
    typeof output.scoreBand === "string" &&
    Array.isArray(output.scoreBreakdown) &&
    Array.isArray(output.launchBlockers) &&
    Array.isArray(output.readySignals) &&
    typeof output.recommendedNextStep === "string" &&
    typeof output.summary === "string"
  );
}

function isMonetisationModelOutput(
  value: unknown,
): value is MonetisationModelOutput {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const output = value as Partial<MonetisationModelOutput>;

  return (
    Boolean(output.recommendedModel) &&
    typeof output.recommendedModel?.label === "string" &&
    typeof output.positioningAngle === "string" &&
    Boolean(output.pricingGuidance) &&
    Boolean(output.offerStructure) &&
    Boolean(output.audienceFit) &&
    Array.isArray(output.alternativeModels) &&
    Array.isArray(output.whatToAvoid) &&
    typeof output.recommendedNextStep === "string" &&
    typeof output.summary === "string"
  );
}

function isSalesHeadlineOutput(value: unknown): value is SalesHeadlineOutput {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const output = value as Partial<SalesHeadlineOutput>;

  return (
    Boolean(output.recommendedTopPick) &&
    typeof output.recommendedTopPick?.headline === "string" &&
    typeof output.recommendedTopPick.reason === "string" &&
    Array.isArray(output.headlines) &&
    Array.isArray(output.positioningNotes) &&
    Array.isArray(output.whatToAvoid) &&
    typeof output.recommendedNextStep === "string" &&
    typeof output.summary === "string"
  );
}

export function ProToolResultDetail() {
  const { id } = useParams();
  const { getToken, isLoaded } = useAuth();
  const [result, setResult] = useState<ToolResultSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  useEffect(() => {
    async function loadResult() {
      if (!isLoaded || !id) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const response = await getProToolResult(token, id);
        setResult(response.result);
      } catch {
        setError("We could not load this result.");
      } finally {
        setIsLoading(false);
      }
    }

    void loadResult();
  }, [getToken, id, isLoaded]);

  const summaryText = useMemo(
    () => (result ? buildSummaryText(result) : ""),
    [result],
  );
  const backPath = result ? getToolRoute(result.tool_type) : "/tools";

  async function handleCopySummary() {
    if (!summaryText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(summaryText);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  }

  function renderResult() {
    if (!result) {
      return null;
    }

    if (result.status === "failed") {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm leading-6 text-red-800">
          This saved result did not complete. Please try again from the tool
          page.
        </div>
      );
    }

    if (
      result.tool_type === "launch_readiness_checker" &&
      isLaunchReadinessOutput(result.output_payload)
    ) {
      return <LaunchReadinessResult output={result.output_payload} />;
    }

    if (
      result.tool_type === "monetisation_model_suggester" &&
      isMonetisationModelOutput(result.output_payload)
    ) {
      return <MonetisationModelResult output={result.output_payload} />;
    }

    if (
      result.tool_type === "sales_page_headline_generator" &&
      isSalesHeadlineOutput(result.output_payload)
    ) {
      return <SalesHeadlineResult output={result.output_payload} />;
    }

    return (
      <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-600">
        This saved result cannot be displayed yet.
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      <Link
        to={backPath}
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to tool
      </Link>

      {isLoading ? (
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
          <div className="h-36 animate-pulse rounded-md bg-slate-100" />
          <p className="mt-4 text-sm font-medium text-slate-500">
            Loading saved results...
          </p>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm leading-6 text-red-800">
          {error}
        </div>
      ) : result ? (
        <>
          <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-indigo-700">
                  Saved Pro tool result
                </p>
                <h2 className="mt-2 break-words text-2xl font-semibold tracking-normal text-slate-950">
                  {result.toolDisplayName}
                </h2>
                <div className="mt-3 flex min-w-0 flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                  <span>Status: {formatStatusLabel(result.status)}</span>
                  <span>{formatDate(result.created_at)}</span>
                  {result.projectTitle ? (
                    <span className="break-words">
                      Project: {result.projectTitle}
                    </span>
                  ) : null}
                  {result.blueprintTitle ? (
                    <span className="break-words">
                      Blueprint: {result.blueprintTitle}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleCopySummary()}
                className="w-fit rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 max-sm:w-full"
              >
                {copyState === "copied" ? "Copied" : "Copy summary"}
              </button>
            </div>
            {copyState === "error" ? (
              <p className="mt-3 text-sm text-red-700">
                Could not copy the summary this time.
              </p>
            ) : null}
          </section>

          {renderResult()}
        </>
      ) : (
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600 sm:p-5">
          This saved result could not be found.
        </div>
      )}
    </div>
  );
}
